import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CategoryIcon from '@mui/icons-material/Category';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ADMIN_COOKIE_KEY } from '../../hooks/useAdmin';
import Cookies from 'js-cookie';

const API_URL = 'https://guide.zerdegroup.kz/api/';

interface Category {
  id: number;
  title: string;
  display_order: number;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: number;
  title: string;
  category_id: number;
  display_order: number;
}

interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  isSubcategory?: boolean;
}

interface SortableListItemProps {
  id: string;
  title: string;
  displayOrder: number;
  icon: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  onToggle?: () => void;
  isExpanded?: boolean;
  isSubcategory?: boolean;
  isUpdating: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, children, isSubcategory }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
};

const SortableListItem: React.FC<SortableListItemProps> = ({
  id,
  title,
  displayOrder,
  icon,
  secondaryIcon,
  onToggle,
  isExpanded,
  isSubcategory,
  isUpdating
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ListItem
        sx={{
          bgcolor: 'background.paper',
          pl: isSubcategory ? 2 : 2,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          cursor: isUpdating ? 'default' : 'grab',
          touchAction: 'none',
        }}
      >
        <Box {...attributes} {...listeners} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <ListItemIcon>
            <DragIndicatorIcon />
          </ListItemIcon>
          <ListItemIcon>
            {icon}
          </ListItemIcon>
          {secondaryIcon && <ListItemIcon>{secondaryIcon}</ListItemIcon>}
          <ListItemText 
            primary={title}
            secondary={`Порядок: ${displayOrder + 1}`}
          />
        </Box>
        {onToggle && (
          <IconButton 
            onClick={onToggle}
            sx={{ ml: 1 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItem>
    </div>
  );
};

const CategorySettings: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openCategories, setOpenCategories] = useState<{ [key: number]: boolean }>({});
  const [isUpdating, setIsUpdating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      const categoriesResponse = await fetch(`${API_URL}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!categoriesResponse.ok) {
        throw new Error('Ошибка при загрузке категорий');
      }

      const categoriesData = await categoriesResponse.json();
      
      const categoriesWithSubcategories = await Promise.all(
        categoriesData.items.map(async (category: Category) => {
          const subcategoriesResponse = await fetch(
            `${API_URL}/subcategories/category/${category.id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (subcategoriesResponse.ok) {
            const subcategories = await subcategoriesResponse.json();
            return {
              ...category,
              subcategories: subcategories.sort((a: Subcategory, b: Subcategory) => 
                a.display_order - b.display_order
              )
            };
          }
          return { ...category, subcategories: [] };
        })
      );

      setCategories(categoriesWithSubcategories.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleCategory = (categoryId: number) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const updateOrder = async (
    items: Category[] | Subcategory[],
    isSubcategory: boolean,
    categoryId?: number
  ) => {
    try {
      setIsUpdating(true);
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      const updates = items.map((item, index) => ({
        id: item.id,
        display_order: index
      }));

      const endpoint = isSubcategory ? 'subcategories' : 'categories';
      
      await Promise.all(
        updates.map(update => 
          fetch(`${API_URL}/${endpoint}/${update.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ display_order: update.display_order })
          })
        )
      );

      if (!isSubcategory) {
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];
          updates.forEach(update => {
            const category = updatedCategories.find(c => c.id === update.id);
            if (category) {
              category.display_order = update.display_order;
            }
          });
          return updatedCategories;
        });
      } else if (categoryId) {
        setCategories(prevCategories => {
          return prevCategories.map(category => {
            if (category.id === categoryId) {
              const updatedSubcategories = [...category.subcategories];
              updates.forEach(update => {
                const subcategory = updatedSubcategories.find(s => s.id === update.id);
                if (subcategory) {
                  subcategory.display_order = update.display_order;
                }
              });
              return {
                ...category,
                subcategories: updatedSubcategories
              };
            }
            return category;
          });
        });
      }
    } catch (err) {
      setError('Ошибка при обновлении порядка');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id === over.id) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    if (activeId.startsWith('category-')) {
      const oldIndex = categories.findIndex(cat => `category-${cat.id}` === activeId);
      const newIndex = categories.findIndex(cat => `category-${cat.id}` === overId);
      
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);
      updateOrder(newCategories, false);
    } else if (activeId.startsWith('subcategory-')) {
      const activeCategory = categories.find(category => 
        category.subcategories.some(sub => `subcategory-${sub.id}` === activeId)
      );

      if (activeCategory) {
        const oldIndex = activeCategory.subcategories.findIndex(
          sub => `subcategory-${sub.id}` === activeId
        );
        const newIndex = activeCategory.subcategories.findIndex(
          sub => `subcategory-${sub.id}` === overId
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newSubcategories = arrayMove(activeCategory.subcategories, oldIndex, newIndex);
          const newCategories = categories.map(c =>
            c.id === activeCategory.id ? { ...c, subcategories: newSubcategories } : c
          );
          
          setCategories(newCategories);
          updateOrder(newSubcategories, true, activeCategory.id);
        }
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        Настройки отображения категорий
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Перетащите элементы для изменения порядка их отображения
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Paper elevation={2}>
          <List sx={{ width: '100%' }}>
            <SortableContext
              items={categories.map(cat => `category-${cat.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {categories.map((category, index) => (
                <React.Fragment key={category.id}>
                  {index > 0 && <Divider />}
                  <SortableListItem
                    id={`category-${category.id}`}
                    title={category.title}
                    displayOrder={category.display_order}
                    icon={<CategoryIcon />}
                    onToggle={() => handleToggleCategory(category.id)}
                    isExpanded={openCategories[category.id]}
                    isUpdating={isUpdating}
                  />
                  <Collapse in={openCategories[category.id]} timeout="auto">
                    <Box sx={{ pl: 4 }}>
                      <SortableContext
                        items={category.subcategories.map(sub => `subcategory-${sub.id}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        {category.subcategories.map((subcategory, subIndex) => (
                          <SortableListItem
                            key={subcategory.id}
                            id={`subcategory-${subcategory.id}`}
                            title={subcategory.title}
                            displayOrder={subcategory.display_order}
                            icon={<FolderIcon />}
                            isSubcategory
                            isUpdating={isUpdating}
                          />
                        ))}
                      </SortableContext>
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </SortableContext>
          </List>
        </Paper>
      </DndContext>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mt: 2, fontStyle: 'italic' }}
      >
        * Изменения сохраняются автоматически
      </Typography>
    </Box>
  );
};

export default CategorySettings; 